import { MoneyDisplay } from "@/ds/custom/MoneyDisplay";
import { cn } from "@/ds/utils";
import { buildMarketValueChartBackdropPaths, type MarketValueChartBackdropPosition } from "@/features/common/components/MarketValueChartBackdropLogic";
import { Padlock } from "@/features/common/components/Padlock";
import { useElementSize } from "@/lib/common/useElementSize";
import type { Money } from "@/lib/domain/Money";
import { MinusIcon, TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import { useId, useMemo, type FC } from "react";

export interface MarketValueDisplayProps {
	marketValue: Money;
	invested: Money | null | undefined;
	gain: Money | null | undefined;
	historicalPositions: readonly MarketValueChartBackdropPosition[];
}

export const MarketValueDisplay: FC<MarketValueDisplayProps> = props => {
	const {
		marketValue,
		invested,
		gain,
		historicalPositions
	} = props;

	const gainValue = gain?.value;
	const trend: "up" | "down" | "flat" = gainValue === undefined || gainValue === 0 ? "flat" : gainValue > 0 ? "up" : "down";

	const trendGradientClassName = trend === "down" ? "from-destructive/10" : trend === "flat" ? "from-muted-foreground/10" : "from-primary/10";

	return (
		<div className={cn("relative overflow-hidden border-b bg-card bg-gradient-to-br to-transparent p-6", trendGradientClassName)}>
			<MarketValueChartBackdrop trend={trend} historicalPositions={historicalPositions} />

			<div className="relative flex flex-wrap items-end justify-between gap-4">
				<div className="text-shadow-sm text-shadow-white/50 dark:text-shadow-black/50">
					<div className="type-overline">Market Value</div>
					<MoneyDisplay mode="Standard" money={marketValue} className="type-hero" />
				</div>

				<Padlock on={gain}>
					{x => <GainBadge gain={x} invested={invested} />}
				</Padlock>
			</div>
		</div>
	);
};

interface GainBadgeProps {
	gain: Money;
	invested: Money | null | undefined;
}

const GainBadge: FC<GainBadgeProps> = props => {
	const { gain, invested } = props;

	const Icon = gain.value > 0 ? TrendingUpIcon : gain.value < 0 ? TrendingDownIcon : MinusIcon;
	const colorClassName = gain.value > 0 ? "bg-primary/10 text-primary" : gain.value < 0 ? "bg-destructive/10 text-destructive" : "bg-muted-foreground/10 text-muted-foreground";

	return (
		<div className={cn("flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold", colorClassName)}>
			<Icon className="size-4" />
			<MoneyDisplay mode="DeltaPercentage" money={gain} originalValue={invested?.value || 0} />
		</div>
	);
};

interface MarketValueChartBackdropProps {
	trend: "up" | "down" | "flat";
	historicalPositions: readonly MarketValueChartBackdropPosition[];
}

const MarketValueChartBackdrop: FC<MarketValueChartBackdropProps> = props => {
	const { trend, historicalPositions } = props;
	const gradientId = useId();
	const [sizeRef, { width, height }] = useElementSize<HTMLDivElement>();

	const paths = useMemo(
		() => buildMarketValueChartBackdropPaths(historicalPositions, width, height),
		[historicalPositions, width, height]
	);

	const colorClassName = trend === "down" ? "text-destructive" : trend === "flat" ? "text-muted-foreground" : "text-primary";

	return (
		<div ref={sizeRef} className={cn("pointer-events-none absolute inset-x-0 bottom-0 h-28 w-full", colorClassName)}>
			{
				paths &&
				<svg aria-hidden="true" viewBox={`0 0 ${width} ${height}`} className="size-full">
					<defs>
						<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="currentColor" stopOpacity="0.25" />
							<stop offset="100%" stopColor="currentColor" stopOpacity="0" />
						</linearGradient>
					</defs>
					<path d={`${paths.marketValuePath} L${width},${height} L0,${height} Z`} fill={`url(#${gradientId})`} stroke="none" />
					<path d={paths.marketValuePath} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
					{
						paths.investedPath &&
						<path d={paths.investedPath} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 3" opacity="0.35" />
					}
				</svg>
			}
		</div>
	);
};
